// Crew display type (Paper "Family"). Page titles use Instrument Serif; everything else Geist.
import type { ComponentProps, ReactNode } from "react";
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";

export type Tone = "foreground" | "copy" | "muted" | "copper" | "moss" | "brick";

export const toneText: Record<Tone, string> = {
  foreground: "text-foreground",
  copy: "text-copy",
  muted: "text-muted-foreground",
  copper: "text-copper",
  moss: "text-moss",
  brick: "text-brick",
};

/** Greeting-size serif headline ("Good morning, Nadia."). */
export function DisplayTitle({ className, ...props }: ComponentProps<"h1">) {
  return (
    <h1
      data-slot="display-title"
      className={cn("font-display text-display tracking-tight text-foreground", className)}
      {...props}
    />
  );
}

/** Serif page title ("Decisions", "Tasks"). */
export function PageTitle({ className, ...props }: ComponentProps<"h1">) {
  return (
    <h1
      data-slot="page-title"
      className={cn("font-display text-page text-foreground", className)}
      {...props}
    />
  );
}

/** Page title, one-line description, trailing actions. */
export function PageHeader({
  title,
  description,
  actions,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div data-slot="page-header" className={cn("flex items-end justify-between gap-6", className)}>
      <div className="flex min-w-0 flex-col gap-1.5">
        <PageTitle>{title}</PageTitle>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Serif section heading with an optional right-aligned aside. */
export function SectionHeading({
  children,
  aside,
  size = "lg",
  className,
}: {
  children: ReactNode;
  aside?: ReactNode;
  size?: "lg" | "md";
  className?: string;
}) {
  return (
    <div
      data-slot="section-heading"
      className={cn("flex items-baseline justify-between gap-4", className)}
    >
      <h2
        className={cn(
          "font-display text-foreground",
          size === "lg" ? "text-title" : "text-[26px] leading-8",
        )}
      >
        {children}
      </h2>
      {aside ? <div className="shrink-0 text-meta text-muted-foreground">{aside}</div> : null}
    </div>
  );
}

/** Small uppercase label ("CHOOSE · FOR YOU", "RECEIPTS"). */
export function Eyebrow({
  children,
  tone = "muted",
  size = "md",
  className,
}: {
  children: ReactNode;
  tone?: Tone;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <span
      data-slot="eyebrow"
      className={cn(
        "font-semibold tracking-label uppercase",
        size === "sm" ? "text-2xs" : "text-xs",
        toneText[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Quiet underlined link ("All activity", "Edit in plain words"). */
export function TextLink({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      className={cn(
        "text-meta text-copy underline decoration-foreground/30 decoration-1 underline-offset-[3px] hover:decoration-foreground",
        className,
      )}
    >
      {children}
    </Link>
  );
}
